import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  ChevronDown,
  Download,
  Plus,
  Search,
  Send,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { FilterChips, LeadStatusBadge, Pagination } from '../components/ui/data';
import { ConfirmDialog, Modal, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  Select,
  Table,
  Td,
  Th,
  Textarea,
  Tr,
} from '../components/ui/primitives';
import {
  useBulkDeleteLeads,
  useBulkLeadAction,
  useImportLeads,
  useLeadStatusCounts,
  useLeadTypes,
  useLeads,
  useSaveLead,
  type LeadFilters,
} from '../hooks/queries';
import { api } from '../lib/api';
import { patchLeadFilters, readLeadFilters } from '../lib/lead-filters';
import { cn, download, formatNumber, formatRelative, titleCase, truncate } from '../lib/utils';
import { LEAD_STATUSES } from '../types/api';
import { LeadDrawer } from './leads/LeadDrawer';
import { QueueOutreachModal } from './leads/QueueOutreachModal';

const STATUS_CHIPS = [
  { value: 'NEW', label: 'New' },
  { value: 'ENRICHED', label: 'Enriched' },
  { value: 'CONTACTED', label: 'Contacted' },
  { value: 'REPLIED', label: 'Replied' },
  { value: 'INTERESTED', label: 'Interested' },
] as const;

const CSV_EXAMPLE = `company,contact,email,website,country,notes
Acme Rentals,Jane Doe,jane@acme.example,https://acme.example,Netherlands,met at trade fair
Bravo Events,,info@bravo.example,https://bravo.example,Belgium,`;

export const LeadsPage = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const filters = readLeadFilters(searchParams);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openLeadId, setOpenLeadId] = useState<string | null>(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading, error, isFetching } = useLeads(projectId, filters);
  const { data: statusCounts } = useLeadStatusCounts(projectId);
  const { data: leadTypes } = useLeadTypes(projectId);
  const bulkAction = useBulkLeadAction(projectId);
  const bulkDelete = useBulkDeleteLeads(projectId);
  const saveLead = useSaveLead(projectId);
  const importLeads = useImportLeads(projectId);

  const leads = data?.items ?? [];
  const allSelected = leads.length > 0 && leads.every((lead) => selected.has(lead.id));

  const patch = (next: Partial<LeadFilters>) => {
    setSelected(new Set());
    setSearchParams(patchLeadFilters(searchParams, next), { replace: true });
  };
  useEffect(() => setSelected(new Set()), [searchParams]);
  const hasFilters = Boolean(filters.status || filters.leadTypeId || filters.search || filters.hasEmail !== undefined);
  const clearFilters = () => patch({ status: undefined, leadTypeId: undefined, search: '', hasEmail: undefined });

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const exportCsv = async () => {
    try {
      const csv = await api.getRaw(
        `/projects/${projectId}/leads/export?${new URLSearchParams({
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.leadTypeId ? { leadTypeId: filters.leadTypeId } : {}),
          ...(filters.search ? { search: filters.search } : {}),
        }).toString()}`,
      );
      download(`leads-${new Date().toISOString().slice(0, 10)}.csv`, csv);
      toast.success('Export downloaded');
    } catch (caught) {
      toast.error(caught);
    }
  };

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Leads"
        description="Find the right contacts, then select leads to queue personalized outreach."
        actions={
          <>
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setAddOpen(true)}>
              Add lead
            </Button>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button>More <ChevronDown className="h-4 w-4" /></Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={6} className="z-50 min-w-44 rounded-xl border border-line bg-surface p-1.5 shadow-pop">
                  <DropdownMenu.Item onSelect={() => setImportOpen(true)} className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2.5 text-[13px] outline-none data-[highlighted]:bg-raised">
                    <Upload className="h-4 w-4 text-muted" /> Import CSV
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={exportCsv} className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2.5 text-[13px] outline-none data-[highlighted]:bg-raised">
                    <Download className="h-4 w-4 text-muted" /> Export CSV
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
          <div className="relative min-w-0 basis-64 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
            <Input
              aria-label="Search leads"
              value={filters.search ?? ''}
              onChange={(event) => patch({ search: event.target.value })}
              placeholder="Search companies, contacts, emails…"
              className="pl-8"
            />
          </div>

          <Select
            aria-label="Filter by lead type"
            value={filters.leadTypeId ?? ''}
            onChange={(event) => patch({ leadTypeId: event.target.value || undefined })}
            className="w-auto min-w-[10rem]"
          >
            <option value="">All lead types</option>
            {(leadTypes ?? []).map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>

          <Select
            aria-label="Filter by email availability"
            value={filters.hasEmail === undefined ? '' : String(filters.hasEmail)}
            onChange={(event) =>
              patch({ hasEmail: event.target.value === '' ? undefined : event.target.value === 'true' })
            }
            className="w-auto"
          >
            <option value="">Any email</option>
            <option value="true">Has email</option>
            <option value="false">Missing email</option>
          </Select>

          <Select
            aria-label="Sort leads"
            value={`${filters.sortBy}:${filters.sortDir}`}
            onChange={(event) => {
              const [sortBy, sortDir] = event.target.value.split(':');
              patch({ sortBy, sortDir: sortDir as 'asc' | 'desc' });
            }}
            className="w-auto"
          >
            <option value="createdAt:desc">Newest first</option>
            <option value="createdAt:asc">Oldest first</option>
            <option value="score:desc">Best score</option>
            <option value="companyName:asc">Company A–Z</option>
            <option value="lastContactedAt:desc">Recently contacted</option>
          </Select>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
          <FilterChips
            options={STATUS_CHIPS as unknown as Array<{ value: string; label: string }>}
            value={filters.status}
            onChange={(status) => patch({ status })}
            counts={statusCounts}
          />
          <Select aria-label="Filter by lead status" className="w-auto" value={filters.status ?? ''} onChange={(event) => patch({ status: event.target.value || undefined })}>
            <option value="">All statuses</option>
            {LEAD_STATUSES.map((status) => <option key={status} value={status}>{titleCase(status)}</option>)}
          </Select>
          {hasFilters ? (
            <Button
              variant="ghost"
              size="sm"
              icon={<X className="h-3.5 w-3.5" />}
              onClick={clearFilters}
            >
              Clear
            </Button>
          ) : null}
        </div>

        {selected.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-line bg-brand/5 px-4 py-2.5">
            <span className="text-[13px] font-medium text-ink">{selected.size} selected</span>
            <Button
              variant="primary"
              size="sm"
              icon={<Send className="h-3.5 w-3.5" />}
              onClick={() => setQueueOpen(true)}
            >
              Queue outreach
            </Button>
            <Select
              aria-label="Set status for selected leads"
              className="h-9 w-auto text-[13px]"
              value=""
              onChange={async (event) => {
                if (!event.target.value) return;
                try {
                  await bulkAction.mutateAsync({ leadIds: selectedIds, status: event.target.value });
                  toast.success(`Moved ${selected.size} lead(s) to ${event.target.value}`);
                  setSelected(new Set());
                } catch (caught) {
                  toast.error(caught);
                }
              }}
            >
              <option value="">Set status…</option>
              {LEAD_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {titleCase(status)}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Set type for selected leads"
              className="h-9 w-auto text-[13px]"
              value=""
              onChange={async (event) => {
                if (!event.target.value) return;
                try {
                  await bulkAction.mutateAsync({
                    leadIds: selectedIds,
                    leadTypeId: event.target.value,
                  });
                  toast.success('Lead type updated');
                  setSelected(new Set());
                } catch (caught) {
                  toast.error(caught);
                }
              }}
            >
              <option value="">Set lead type…</option>
              {(leadTypes ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Select>
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
            <div className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </div>
        ) : null}

        {error ? (
          <div className="p-4">
            <ErrorBlock error={error} />
          </div>
        ) : leads.length === 0 ? (
          <EmptyState
            icon={<Users className="h-5 w-5" />}
            title={hasFilters ? 'No matching leads' : 'Build your lead list'}
            description={
              hasFilters
                ? 'Try a different search or clear your filters to see all leads.'
                : 'Queue a research job from Lead types, import a CSV, or add a lead by hand.'
            }
            action={
              <Button variant="primary" onClick={hasFilters ? clearFilters : () => setAddOpen(true)}>
                {hasFilters ? 'Clear filters' : 'Add a lead'}
              </Button>
            }
          />
        ) : (
          <>
            <div aria-busy={isFetching} className={cn('transition-opacity', isFetching && 'opacity-60')}>
              <Table>
                <thead>
                  <tr>
                    <Th className="w-9">
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        className="h-3.5 w-3.5 cursor-pointer accent-[rgb(var(--brand))]"
                        checked={allSelected}
                        disabled={isFetching}
                        ref={(node) => { if (node) node.indeterminate = !allSelected && leads.some((lead) => selected.has(lead.id)); }}
                        onChange={() =>
                          setSelected(allSelected ? new Set() : new Set(leads.map((lead) => lead.id)))
                        }
                      />
                    </Th>
                    <Th>Company</Th>
                    <Th>Contact</Th>
                    <Th>Type</Th>
                    <Th>Status</Th>
                    <Th className="text-right">Score</Th>
                    <Th>Location</Th>
                    <Th>Added</Th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <Tr key={lead.id} className={cn("cursor-pointer", selected.has(lead.id) && "bg-brand/5")}>
                      <Td onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${lead.companyName}`}
                          className="h-3.5 w-3.5 cursor-pointer accent-[rgb(var(--brand))]"
                          disabled={isFetching}
                          checked={selected.has(lead.id)}
                          onChange={() => toggle(lead.id)}
                        />
                      </Td>
                      <Td onClick={() => setOpenLeadId(lead.id)}>
                        <button type="button" onClick={() => setOpenLeadId(lead.id)} className="block max-w-[15rem] truncate rounded text-left font-semibold text-ink hover:text-brand hover:underline">
                          {lead.companyName}
                        </button>
                        <div className="max-w-[15rem] truncate text-xs text-faint">
                          {lead.domain ?? '—'}
                        </div>
                      </Td>
                      <Td onClick={() => setOpenLeadId(lead.id)}>
                        {lead.contactName ? (
                          <>
                            <div className="max-w-[13rem] truncate text-ink">{lead.contactName}</div>
                            <div className="max-w-[13rem] truncate text-xs text-faint">
                              {lead.email ?? 'no email'}
                            </div>
                          </>
                        ) : lead.email ? (
                          <span className="block max-w-[13rem] truncate text-muted">{lead.email}</span>
                        ) : (
                          <span className="text-faint">—</span>
                        )}
                      </Td>
                      <Td onClick={() => setOpenLeadId(lead.id)}>
                        {lead.leadType ? (
                          <Badge dot={lead.leadType.color}>{truncate(lead.leadType.name, 20)}</Badge>
                        ) : (
                          <span className="text-faint">—</span>
                        )}
                      </Td>
                      <Td onClick={() => setOpenLeadId(lead.id)}>
                        <LeadStatusBadge status={lead.status} />
                      </Td>
                      <Td className="text-right tabular-nums" onClick={() => setOpenLeadId(lead.id)}>
                        {lead.score ?? <span className="text-faint">—</span>}
                      </Td>
                      <Td className="whitespace-nowrap text-muted" onClick={() => setOpenLeadId(lead.id)}>
                        {[lead.city, lead.country].filter(Boolean).join(', ') || '—'}
                      </Td>
                      <Td className="whitespace-nowrap text-faint" onClick={() => setOpenLeadId(lead.id)}>
                        {formatRelative(lead.createdAt)}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <Pagination
              page={data?.page ?? 1}
              totalPages={data?.totalPages ?? 1}
              total={data?.total ?? 0}
              pageSize={data?.pageSize ?? 25}
              onPage={(page) => patch({ page })}
            />
          </>
        )}
      </Card>

      <LeadDrawer leadId={openLeadId} projectId={projectId} onClose={() => setOpenLeadId(null)} />

      <QueueOutreachModal
        open={queueOpen}
        onClose={() => setQueueOpen(false)}
        projectId={projectId}
        leadIds={selectedIds}
        onQueued={() => setSelected(new Set())}
      />

      <AddLeadModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={async (values) => {
          try {
            await saveLead.mutateAsync(values);
            toast.success('Lead added');
            setAddOpen(false);
          } catch (caught) {
            toast.error(caught);
          }
        }}
        saving={saveLead.isPending}
        leadTypes={(leadTypes ?? []).map((type) => ({ id: type.id, name: type.name }))}
      />

      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import leads from CSV"
        description="Headers are matched loosely: company, contact, email, website, country, city, industry, score, tags, notes. Duplicates are skipped automatically."
        footer={
          <Button variant="ghost" onClick={() => setImportOpen(false)}>
            Close
          </Button>
        }
      >
        <ImportForm
          leadTypes={(leadTypes ?? []).map((type) => ({ id: type.id, name: type.name }))}
          busy={importLeads.isPending}
          onSubmit={async (values) => {
            try {
              const result = await importLeads.mutateAsync(values);
              toast.success(
                `Imported ${result.summary.created} new lead(s); ${result.summary.duplicates} duplicate(s) skipped.`,
              );
              setImportOpen(false);
            } catch (caught) {
              toast.error(caught);
            }
          }}
        />
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete leads"
        destructive
        confirmLabel={`Delete ${selected.size}`}
        loading={bulkDelete.isPending}
        message={`Permanently delete ${selected.size} lead(s)? Their emails are removed too.`}
        onConfirm={async () => {
          try {
            const result = await bulkDelete.mutateAsync(selectedIds);
            toast.success(`Deleted ${result.deleted} lead(s)`);
            setSelected(new Set());
            setConfirmDelete(false);
          } catch (caught) {
            toast.error(caught);
          }
        }}
      />
    </Page>
  );
};

const AddLeadModal = ({
  open,
  onClose,
  onSubmit,
  saving,
  leadTypes,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void;
  saving: boolean;
  leadTypes: Array<{ id: string; name: string }>;
}) => {
  const [form, setForm] = useState({
    companyName: '',
    contactName: '',
    title: '',
    email: '',
    website: '',
    country: '',
    leadTypeId: '',
    rationale: '',
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a lead"
      description="A company name, an email or a website is enough. Duplicates are refused by the database."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={saving}
            onClick={() =>
              onSubmit({
                companyName: form.companyName.trim() || form.website || form.email,
                contactName: form.contactName || undefined,
                title: form.title || undefined,
                email: form.email || undefined,
                website: form.website || undefined,
                country: form.country || undefined,
                leadTypeId: form.leadTypeId || undefined,
                rationale: form.rationale || undefined,
                source: 'manual',
              })
            }
          >
            Add lead
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Company" htmlFor="new-company" className="sm:col-span-2">
          <Input
            id="new-company"
            autoFocus
            value={form.companyName}
            onChange={(event) => setForm({ ...form, companyName: event.target.value })}
          />
        </Field>
        <Field label="Contact name" htmlFor="new-contact">
          <Input
            id="new-contact"
            value={form.contactName}
            onChange={(event) => setForm({ ...form, contactName: event.target.value })}
          />
        </Field>
        <Field label="Job title" htmlFor="new-title">
          <Input
            id="new-title"
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
        </Field>
        <Field label="Email" htmlFor="new-email">
          <Input
            id="new-email"
            type="email"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
          />
        </Field>
        <Field label="Website" htmlFor="new-website">
          <Input
            id="new-website"
            value={form.website}
            onChange={(event) => setForm({ ...form, website: event.target.value })}
          />
        </Field>
        <Field label="Country" htmlFor="new-country">
          <Input
            id="new-country"
            value={form.country}
            onChange={(event) => setForm({ ...form, country: event.target.value })}
          />
        </Field>
        <Field label="Lead type" htmlFor="new-type">
          <Select
            id="new-type"
            value={form.leadTypeId}
            onChange={(event) => setForm({ ...form, leadTypeId: event.target.value })}
          >
            <option value="">Unassigned</option>
            {leadTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Why they fit"
          htmlFor="new-rationale"
          className="sm:col-span-2"
          hint="The outreach agent uses this to personalise the email."
        >
          <Textarea
            id="new-rationale"
            rows={3}
            value={form.rationale}
            onChange={(event) => setForm({ ...form, rationale: event.target.value })}
          />
        </Field>
      </div>
    </Modal>
  );
};

const ImportForm = ({
  leadTypes,
  busy,
  onSubmit,
}: {
  leadTypes: Array<{ id: string; name: string }>;
  busy: boolean;
  onSubmit: (values: { csv: string; leadTypeId?: string; source?: string }) => void;
}) => {
  const [csv, setCsv] = useState('');
  const [leadTypeId, setLeadTypeId] = useState('');

  return (
    <div className="space-y-4">
      <Field label="Assign to lead type" htmlFor="import-type">
        <Select
          id="import-type"
          value={leadTypeId}
          onChange={(event) => setLeadTypeId(event.target.value)}
        >
          <option value="">Unassigned</option>
          {leadTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Upload a file" htmlFor="import-file">
        <input
          id="import-file"
          type="file"
          accept=".csv,text/csv"
          className="block w-full cursor-pointer rounded-lg border border-line bg-surface p-2 text-[13px] file:mr-3 file:rounded-md file:border-0 file:bg-raised file:px-3 file:py-1.5 file:text-[13px] file:text-ink"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (file) setCsv(await file.text());
          }}
        />
      </Field>

      <Field label="Or paste CSV" htmlFor="import-csv" hint={`Example:\n${CSV_EXAMPLE}`}>
        <Textarea
          id="import-csv"
          rows={8}
          className="prompt-input"
          value={csv}
          placeholder={CSV_EXAMPLE}
          onChange={(event) => setCsv(event.target.value)}
        />
      </Field>

      <Button
        variant="primary"
        className="w-full"
        loading={busy}
        disabled={!csv.trim()}
        onClick={() => onSubmit({ csv, leadTypeId: leadTypeId || undefined, source: 'csv' })}
      >
        Import {csv.trim() ? `${formatNumber(csv.trim().split('\n').length - 1)} row(s)` : ''}
      </Button>
    </div>
  );
};
